import {useMutation, useQuery, useQueryClient} from 'react-query';
import {
  CurrentUserData,
  Folder,
  Media,
  EditorProject,
  EditorLayer,
  FolderType,
  FolderPreviewContentType,
} from '../types';
import {useMediaPlayer} from './useMediaPlayer';
import {useContext, useMemo, useRef, useState} from 'react';
import {UserDataDispatch, UserDataState} from '../ctx';
import {UserDataAction, UserDataActionType} from './useUserData';
import {TextInput} from 'react-native-gesture-handler';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {useRoute} from '@react-navigation/native';
import {Clipboard} from 'react-native';
import {
  DocumentPicker,
  domain,
  genMnemonic,
  getById as getByIdMain,
  writeItem as writeItemMain,
  getFolderContent as getFolderContentMain,
  getUserData,
  removeObject,
  writeItem,
  getById,
} from '../api';
import {
  setAppDialogParams,
  setFolderMenuItemId,
  setToastText,
} from '../store/slices/ui';
import {useCloseDialog} from './useCloseDialog';
import {setEditingFolder} from '../store/slices/folders';

export function useFolderMutations(
  folderType: FolderType,
  content?: FolderPreviewContentType[],
  folderContentQuery?: ReturnType<typeof useQuery>,
) {
  const dispatch = useAppDispatch();
  const {closeDialog} = useCloseDialog();
  const queryClient = useQueryClient();
  const isEditingFolder = useAppSelector(
    state => state.folders.isEditingFolder,
  );
  const isEditingFolderDraft = useAppSelector(
    state => state.folders.isEditingFolderDraft,
  );
  const writeItemMutation = useMutation(
    async (item: FolderPreviewContentType) => await writeItem(item),
  );

  const deleteFolderMutation = useMutation(
    async (folderId: string) => {
      const folder = (await getById(folderId)) as Folder;
      try {
        await removeObject(folder);
      } catch (err) {
        console.error('removeObject error', err);
      }

      await writeItem({
        ...folder,
        isDeleted: true,
      });
      folderContentQuery?.refetch();
      queryClient.refetchQueries('folderContent_' + folderId);
      queryClient.refetchQueries('folderContent_' + folder.folderId);
      queryClient.refetchQueries(['projectLayers', folder.folderId]);
    },
  );
  const updateFolderTitleMutation = useMutation(
    async (folder: Folder) => await writeItem(folder),
  );
  const onRemoveFolder = async (folder: Folder) => {
    console.info('onRemoveFolder', folder);
    dispatch(setFolderMenuItemId({itemId: undefined}));
    dispatch(
      setAppDialogParams({
        appDialogVisible: true,
        appDialogTitle: 'Delete Folder',
        appDialogIcon: 'delete',
        appDialogOnSelectRight: async () => {
          await deleteFolderMutation.mutateAsync(folder.id);
          closeDialog();
        },
        appDialogOnSelectLeftt: undefined,
        appDialogBody:
          'This folder will remain for other users who may have access',
        appDialogControls: undefined,
        appDialogLoading: false,
      }),
    );
    console.info('after delete mutation');
  };

  const abortEditFolder = (fId: string) =>
    dispatch(setEditingFolder({[fId]: false}));

  const editFolder = (fId: string) =>
    dispatch(setEditingFolder({[fId]: true}));

  const onFolderActionPress = async (folderId: string) => {
    console.info(
      'isEditingFolder: ' + isEditingFolder[folderId],
      folderId,
    );
    if (isEditingFolder[folderId] === true) {
      const folder = (await getById(folderId)) as Folder;
      folder.title = isEditingFolderDraft[folderId];
      await updateFolderTitleMutation.mutateAsync(folder);
      abortEditFolder(folderId);
      await queryClient.refetchQueries('folderData_' + folderId);
      return;
    }
    editFolder(folderId);
  };

  const onReordered = async (start: number, end: number) => {
    console.info('dragend', start, end);
    if (!content) return;

    if (start === end) return;
    content[start].orderId = end + 1;
    const didEnd = content[end]
      ? (content[end].orderId = start + 1)
      : undefined;

    await writeItemMutation.mutateAsync(content[start]);
    didEnd ? await writeItemMutation.mutateAsync(content[end]) : null;
    await folderContentQuery?.refetch();
  };

  const onShareFolder = async (folder: Folder) => {
    dispatch(setFolderMenuItemId({itemId: undefined}));
    const {id, title, userId} = folder;
    const folderPref =
      domain +
      'webapp/' +
      folderType +
      '/folder/' +
      id +
      '?title=' +
      encodeURIComponent(title) +
      '&userId=' +
      userId +
      '&mnemonic=';
    if (folder.mnemonic) {
      console.info('folder has mnemonic');
      const folderUrl =
        folderPref + encodeURIComponent(folder.mnemonic);
      console.info(folderUrl);
      Clipboard.setString(folderUrl);
    }
    if (!folder.mnemonic) {
      const mnemonic = genMnemonic();
      folder.mnemonic = mnemonic;
      await updateFolderTitleMutation.mutateAsync(folder);
      const folderUrl = folderPref + encodeURIComponent(mnemonic);
      console.info(folderUrl);
      Clipboard.setString(folderUrl);
    }
    dispatch(setToastText('Link to folder has been copied'));
  };
  return {
    onReordered,
    onFolderActionPress,
    editFolder,
    abortEditFolder,
    onRemoveFolder,
    writeItemMutation,
    updateFolderTitleMutation,
    onShareFolder,
  };
}
